/*
  La Sura - "Send this cake" and "Carry on where you left off" (blocks/lasura-cake-share.liquid).

  Choices come from window.lasuraCakeChoices (assets/lasura-cake-prefill.js), the same URL
  parameters as the builder's other carried links, so a sent link or a Carry on rebuilds the
  cake through the prefill that already runs on page load. Only option choices: never the
  message, photos, phone or a name on a topper.

  Kept progress is saved only after the shopper changes something themselves (not the defaults
  the page opens with), in this browser only: across visits when preferences are allowed in the
  cookie banner (Shopify's Customer Privacy API), otherwise for this tab's session.
*/
const PARAMS_IGNORED = ['variant', 'view', 'edit'];
const MAX_AGE = 30 * 864e5;

const store = () => {
  try {
    if (window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.()) return window.localStorage;
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const choices = () => (typeof window.lasuraCakeChoices === 'function' ? window.lasuraCakeChoices() : null);

/** The builder URL for these choices, keeping the layout (?view=) the shopper is on. */
const linkFor = (params) => {
  const url = new URL(location.pathname, location.origin);
  const view = new URLSearchParams(location.search).get('view');
  if (view) url.searchParams.set('view', view);
  params.forEach((value, key) => url.searchParams.set(key, value));
  return url.toString();
};

class LasuraCakeShare extends HTMLElement {
  connectedCallback() {
    this.key = `lasura-cake-progress:${this.dataset.product}`;
    if (this.dataset.part === 'resume') this.setupResume();
    else this.setupSend();
    this.watch();
  }

  /* Save after the shopper's own changes in the builder or the size and height picker. */
  watch() {
    if (window.lasuraCakeShareWatching) return;
    window.lasuraCakeShareWatching = true;
    if (new URLSearchParams(location.search).has('edit')) return;
    let timer;
    const save = () => {
      const params = choices();
      const s = store();
      if (!params || !s) return;
      try {
        /* n: how many steps the builder has (the size and height picker, then each choice group), so
           "Carry on with your cake" can say "3 of 11 steps done" (redesign L7, board BuilderPlacements 6). */
        const n = document.querySelectorAll('variant-picker fieldset, lasura-cake-builder [data-builder-group][data-group-label]').length;
        if ([...params.keys()].length) s.setItem(this.key, JSON.stringify({ t: Date.now(), q: params.toString(), n }));
      } catch {}
    };
    document.addEventListener('change', (event) => {
      if (!event.isTrusted) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest('lasura-cake-builder, variant-picker')) return;
      clearTimeout(timer);
      timer = setTimeout(save, 500);
    });
  }

  setupResume() {
    const here = new URLSearchParams(location.search);
    /* Not while changing a basket cake: that's this cake, not the earlier one. */
    if (here.has('edit') || [...here.keys()].some((k) => !PARAMS_IGNORED.includes(k))) return;
    const s = store();
    let saved;
    try {
      saved = JSON.parse(s?.getItem(this.key) || 'null');
    } catch {}
    if (!saved?.q || Date.now() - saved.t > MAX_AGE) return;

    const note = this.querySelector('[data-share-note]')?.innerHTML.trim() || '';
    this.querySelector('[data-share-go]')?.addEventListener('click', () => {
      location.href = linkFor(new URLSearchParams(saved.q));
    });
    this.querySelector('[data-share-reset]')?.addEventListener('click', () => {
      try {
        s.removeItem(this.key);
      } catch {}
      this.hidden = true;
    });
    this.hidden = false;
    /* Filled after it's shown, so screen readers announce it. */
    const text = this.querySelector('[data-share-text]');
    const holder = document.createElement('div');
    holder.innerHTML = note;
    requestAnimationFrame(() => {
      if (text) text.textContent = holder.textContent;
    });
  }

  setupSend() {
    const button = this.querySelector('[data-share-send]');
    const status = this.querySelector('[data-share-status]');
    const fallback = this.querySelector('[data-share-fallback]');
    const field = this.querySelector('[data-share-url]');
    if (!button) return;
    this.hidden = false;

    button.addEventListener('click', async () => {
      const params = choices();
      const url = linkFor(params || new URLSearchParams());
      if (status) status.textContent = '';
      if (navigator.share) {
        try {
          await navigator.share({ title: this.dataset.shareTitle || document.title, url });
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }
      try {
        await navigator.clipboard.writeText(url);
        if (status) status.textContent = this.dataset.copied || '';
        return;
      } catch {}
      if (fallback && field) {
        field.value = url;
        fallback.hidden = false;
        field.focus();
        field.select();
      }
    });
  }
}

if (!customElements.get('lasura-cake-share')) customElements.define('lasura-cake-share', LasuraCakeShare);
